import { AadeBookInvoiceType, InvoiceRowType } from '../../interfaces/myData.interface';

const getChild = (el: Element | undefined, localName: string): Element | undefined => {
  if (!el) return undefined;
  for (let i = 0; i < el.children.length; i++) {
    if (el.children[i].localName === localName) return el.children[i];
  }
  return undefined;
};

const getChildren = (el: Element | undefined, localName: string): Element[] => {
  if (!el) return [];
  const result: Element[] = [];
  for (let i = 0; i < el.children.length; i++) {
    if (el.children[i].localName === localName) result.push(el.children[i]);
  }
  return result;
};

const getText = (el: Element | undefined, localName: string): string | undefined => {
  const child = getChild(el, localName);
  const text = child?.textContent;
  return text != null && text !== '' ? text : undefined;
};

const getNum = (el: Element | undefined, localName: string): number | undefined => {
  const text = getText(el, localName);
  if (text === undefined) return undefined;
  const n = Number(text);
  return isNaN(n) ? undefined : n;
};

const parseParty = (el: Element | undefined) => {
  const addressEl = getChild(el, 'address');
  return {
    vatNumber: getText(el, 'vatNumber') ?? '',
    country: getText(el, 'country') ?? '',
    branch: getNum(el, 'branch') ?? 0,
    name: getText(el, 'name') ?? '',
    address: {
      street: getText(addressEl, 'street'),
      postalCode: getText(addressEl, 'postalCode') ?? '',
      city: getText(addressEl, 'city') ?? '',
    },
    documentIdNo: getText(el, 'documentIdNo') ?? '',
    supplyAccountNo: getText(el, 'supplyAccountNo') ?? '',
    countryDocumentId: getText(el, 'countryDocumentId') ?? '',
  };
};

const parseClassification = (classEl: Element) => ({
  'icls:classificationType': getText(classEl, 'classificationType'),
  'icls:classificationCategory': getText(classEl, 'classificationCategory'),
  'icls:amount': getNum(classEl, 'amount') ?? 0,
});

const parseClassifications = (els: Element[]) => {
  if (els.length === 0) return undefined;
  const parsed = els.map(parseClassification);
  return parsed.length === 1 ? parsed[0] : parsed;
};

const parseInvoiceDetail = (el: Element): InvoiceRowType => ({
  lineNumber: getNum(el, 'lineNumber') ?? 0,
  recType: getNum(el, 'recType'),
  itemCode: getText(el, 'itemCode'),
  itemDescr: getText(el, 'itemDescr'),
  quantity: getNum(el, 'quantity'),
  measurementUnit: getNum(el, 'measurementUnit'),
  netValue: getNum(el, 'netValue') ?? 0,
  vatCategory: getNum(el, 'vatCategory') ?? 0,
  vatAmount: getNum(el, 'vatAmount') ?? 0,
  incomeClassification: parseClassifications(getChildren(el, 'incomeClassification')) as any,
});

export const parseProviderInvoiceXml = (xmlString: string): AadeBookInvoiceType | null => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    if (xmlDoc.getElementsByTagName('parsererror').length > 0) return null;

    // Handle namespace — find <invoice> by localName
    let invoiceEl: Element | undefined;
    const allEls = xmlDoc.getElementsByTagName('*');
    for (let i = 0; i < allEls.length; i++) {
      if (allEls[i].localName === 'invoice') {
        invoiceEl = allEls[i];
        break;
      }
    }
    if (!invoiceEl) return null;

    const headerEl = getChild(invoiceEl, 'invoiceHeader');
    const summaryEl = getChild(invoiceEl, 'invoiceSummary');
    const paymentMethodsEl = getChild(invoiceEl, 'paymentMethods');

    const paymentMethodDetailEls = getChildren(paymentMethodsEl, 'paymentMethodDetails');
    const paymentDetails = paymentMethodDetailEls.map((d) => ({
      type: getNum(d, 'type') ?? 0,
      amount: getNum(d, 'amount') ?? 0,
      paymentMethodInfo: getText(d, 'paymentMethodInfo'),
    }));

    const detailEls = getChildren(invoiceEl, 'invoiceDetails');
    const invoiceDetails = detailEls.length === 1 ? parseInvoiceDetail(detailEls[0]) : detailEls.map(parseInvoiceDetail);

    return {
      uid: getText(invoiceEl, 'uid') ?? '',
      mark: getNum(invoiceEl, 'mark') ?? 0,
      authenticationCode: getText(invoiceEl, 'authenticationCode'),
      issuer: parseParty(getChild(invoiceEl, 'issuer')) as any,
      counterpart: parseParty(getChild(invoiceEl, 'counterpart')) as any,
      invoiceHeader: {
        series: getText(headerEl, 'series') ?? '',
        aa: getText(headerEl, 'aa') ?? '',
        issueDate: new Date(getText(headerEl, 'issueDate') ?? '') as any,
        invoiceType: getText(headerEl, 'invoiceType') ?? '',
        currency: getText(headerEl, 'currency'),
        vatPaymentSuspension: getText(headerEl, 'vatPaymentSuspension') === 'true' || undefined,
        vehicleNumber: getText(headerEl, 'vehicleNumber'),
        movePurpose: getNum(headerEl, 'movePurpose'),
        isDeliveryNote: getText(headerEl, 'isDeliveryNote') === 'true' || undefined,
        dispatchDate: getText(headerEl, 'dispatchDate'),
        dispatchTime: getText(headerEl, 'dispatchTime'),
      } as any,
      paymentMethods:
        paymentDetails.length > 0
          ? { paymentMethodDetails: paymentDetails.length === 1 ? paymentDetails[0] : (paymentDetails as any) }
          : undefined,
      invoiceDetails: invoiceDetails as any,
      invoiceSummary: {
        totalNetValue: getNum(summaryEl, 'totalNetValue') ?? 0,
        totalVatAmount: getNum(summaryEl, 'totalVatAmount') ?? 0,
        totalWithheldAmount: getNum(summaryEl, 'totalWithheldAmount') ?? 0,
        totalFeesAmount: getNum(summaryEl, 'totalFeesAmount') ?? 0,
        totalStampDutyAmount: getNum(summaryEl, 'totalStampDutyAmount') ?? 0,
        totalOtherTaxesAmount: getNum(summaryEl, 'totalOtherTaxesAmount') ?? 0,
        totalDeductionsAmount: getNum(summaryEl, 'totalDeductionsAmount') ?? 0,
        totalGrossValue: getNum(summaryEl, 'totalGrossValue') ?? 0,
        incomeClassification: parseClassifications(getChildren(summaryEl, 'incomeClassification')) as any,
      },
      downloadingInvoiceUrl: getText(invoiceEl, 'downloadingInvoiceUrl'),
    };
  } catch {
    return null;
  }
};
